import { request } from 'graphql-request';
import type { DocumentNode } from 'graphql';

type PendingEntry<Ctx, Item, Ui> = {
  ctx: Ctx;
  item: Item;
  resolve: (value: Ui) => void;
  reject: (reason: unknown) => void;
};

export type CreateGqlBatcherOptions<Ctx, Item, Resp, Ui> = {
  windowMs: number;
  maxBatch: number;
  query: DocumentNode | string;
  batchKey: (ctx: Ctx) => string;
  buildVariables: (ctx: Ctx, items: Item[]) => Record<string, unknown>;
  parseResponse: (resp: Resp, items: Item[]) => Map<string, Ui>;
  itemRequestId: (item: Item) => string;
  endpoint: (ctx: Ctx) => string;
  authToken: (ctx: Ctx) => string;
};

export function createGqlBatcher<Ctx, Item, Resp, Ui>(
  opts: CreateGqlBatcherOptions<Ctx, Item, Resp, Ui>,
): { enqueue: (ctx: Ctx, item: Item) => Promise<Ui> } {
  const pendingQueues = new Map<string, PendingEntry<Ctx, Item, Ui>[]>();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();

  async function runBatch(
    entries: PendingEntry<Ctx, Item, Ui>[],
  ): Promise<void> {
    if (!entries.length) {
      return;
    }

    const ctx = entries[0].ctx;
    const items = entries.map((e) => e.item);

    try {
      const data = await request<Resp>(
        opts.endpoint(ctx),
        opts.query,
        opts.buildVariables(ctx, items),
        { authorization: `Bearer ${opts.authToken(ctx)}` },
      );

      const byRequestId = opts.parseResponse(data, items);

      for (const entry of entries) {
        const value = byRequestId.get(opts.itemRequestId(entry.item));
        if (value === undefined) {
          entry.reject(new Error('missing batch response row'));
        } else {
          entry.resolve(value);
        }
      }
    } catch (error) {
      for (const entry of entries) {
        entry.reject(error);
      }
    }
  }

  function enqueue(ctx: Ctx, item: Item): Promise<Ui> {
    return new Promise((resolve, reject) => {
      const key = opts.batchKey(ctx);
      let queue = pendingQueues.get(key);
      if (!queue) {
        queue = [];
        pendingQueues.set(key, queue);
      }

      queue.push({ ctx, item, resolve, reject });

      const flushNow = () => {
        const t = timers.get(key);
        if (t !== undefined) {
          clearTimeout(t);
          timers.delete(key);
        }
        const batch = pendingQueues.get(key) ?? [];
        pendingQueues.delete(key);
        void runBatch(batch);
      };

      if (queue.length >= opts.maxBatch) {
        flushNow();
        return;
      }

      if (!timers.has(key)) {
        timers.set(
          key,
          setTimeout(() => {
            timers.delete(key);
            const batch = pendingQueues.get(key) ?? [];
            pendingQueues.delete(key);
            void runBatch(batch);
          }, opts.windowMs),
        );
      }
    });
  }

  return { enqueue };
}
